import React from "react";
import getAxios from "../../hooks/useAxios";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AlertIcon,
  Text,
  Box,
  Button,
  Flex,
  Input,
  Spinner,
  useTheme,
} from "@chakra-ui/react";
import Cookies from "js-cookie";
import { FaArrowRight } from "react-icons/fa";
import { Theme } from "../../theme";

interface RideFare {
  id: string;
  base_fare: number;
  day_rate_per_km: number;
  night_rate_per_km: number;
  night_start_time: string;
  night_end_time: string;
  slab1_multiplier: number;
  slab2_multiplier: number;
  pickup_rate_per_km: number;
  updated_at: string;
}

const ModifyRideFare = () => {
  const axios = getAxios();
  const navigate = useNavigate();
  const theme = useTheme<Theme>();

  const access_token = Cookies.get("x-admin-auth");

  const [rideFare, setRideFare] = React.useState<RideFare>({
    id: "",
    base_fare: 0,
    day_rate_per_km: 0,
    night_rate_per_km: 0,
    night_start_time: "",
    night_end_time: "",
    slab1_multiplier: 0,
    slab2_multiplier: 0,
    pickup_rate_per_km: 0,
    updated_at: "",
  });

  const [isDataFetching, setIsDataFetching] = React.useState(true);

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [createStatus, setCreateStatus] = React.useState("");

  const getRideFare = async () => {
    try {
      const res = await axios.get<RideFare>(`/pricing-config`, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      setRideFare(res.data);
    } catch (error) {
      console.log(error);
      setCreateStatus("error");
    } finally {
      setIsDataFetching(false);
    }
  };

  React.useEffect(() => {
    getRideFare();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setRideFare((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleModifyRideFare = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsSubmitting(true);
    setCreateStatus("");

    const data = {
      base_fare: Number(rideFare.base_fare),
      day_rate_per_km: Number(rideFare.day_rate_per_km),
      night_rate_per_km: Number(rideFare.night_rate_per_km),
      night_start_time: rideFare.night_start_time,
      night_end_time: rideFare.night_end_time,
      slab1_multiplier: Number(rideFare.slab1_multiplier),
      slab2_multiplier: Number(rideFare.slab2_multiplier),
      pickup_rate_per_km: Number(rideFare.pickup_rate_per_km),
    };

    try {
      const res = await axios.put(`/pricing-config`, data, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      console.log("submit", res.data);

      setCreateStatus("created");

      setTimeout(() => {
        navigate("/ride-fare");
      }, 1500);
    } catch (error) {
      console.log(error);
      setCreateStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isDataFetching) {
    return (
      <Flex justifyContent="center" alignItems="center" height="300px">
        <Spinner />
      </Flex>
    );
  }

  return (
    <Box p="2rem">
      <Text fontSize="1.5rem" fontWeight="600" mb="2rem">
        Update Ride Fare
      </Text>

      <Box
        width="60%"
        backgroundColor="white"
        padding="2rem"
        borderRadius="8px"
        border="1px solid"
        borderColor="gray.200"
      >
        <form onSubmit={handleModifyRideFare}>
          <Flex flexDirection="column" gap="1.25rem">
            {/* Base Fare */}
            <Box>
              <Text mb="0.5rem" fontSize="0.9rem" fontWeight="500">
                Base Fare
              </Text>

              <Input
                type="number"
                name="base_fare"
                value={rideFare.base_fare}
                onChange={handleChange}
                placeholder="Enter base fare"
              />
            </Box>

            {/* Day Rate */}
            <Box>
              <Text mb="0.5rem" fontSize="0.9rem" fontWeight="500">
                Day Rate Per KM
              </Text>

              <Input
                type="number"
                name="day_rate_per_km"
                value={rideFare.day_rate_per_km}
                onChange={handleChange}
                placeholder="Enter day rate"
              />
            </Box>

            {/* Night Rate */}
            <Box>
              <Text mb="0.5rem" fontSize="0.9rem" fontWeight="500">
                Night Rate Per KM
              </Text>

              <Input
                type="number"
                name="night_rate_per_km"
                value={rideFare.night_rate_per_km}
                onChange={handleChange}
                placeholder="Enter night rate"
              />
            </Box>

            {/* Pickup Rate */}
            <Box>
              <Text mb="0.5rem" fontSize="0.9rem" fontWeight="500">
                Pickup Rate Per KM
              </Text>

              <Input
                type="number"
                name="pickup_rate_per_km"
                value={rideFare.pickup_rate_per_km}
                onChange={handleChange}
                placeholder="Enter pickup rate"
              />
            </Box>

            {/* Night Start */}
            <Box>
              <Text mb="0.5rem" fontSize="0.9rem" fontWeight="500">
                Night Start Time
              </Text>

              <Input
                type="time"
                name="night_start_time"
                value={rideFare.night_start_time}
                onChange={handleChange}
              />
            </Box>

            {/* Night End */}
            <Box>
              <Text mb="0.5rem" fontSize="0.9rem" fontWeight="500">
                Night End Time
              </Text>

              <Input
                type="time"
                name="night_end_time"
                value={rideFare.night_end_time}
                onChange={handleChange}
              />
            </Box>

            {/* Slab 1 */}
            <Box>
              <Text mb="0.5rem" fontSize="0.9rem" fontWeight="500">
                Slab 1 Multiplier
              </Text>

              <Input
                type="number"
                step="0.01"
                name="slab1_multiplier"
                value={rideFare.slab1_multiplier}
                onChange={handleChange}
                placeholder="Enter slab 1 multiplier"
              />
            </Box>

            {/* Slab 2 */}
            <Box>
              <Text mb="0.5rem" fontSize="0.9rem" fontWeight="500">
                Slab 2 Multiplier
              </Text>

              <Input
                type="number"
                step="0.01"
                name="slab2_multiplier"
                value={rideFare.slab2_multiplier}
                onChange={handleChange}
                placeholder="Enter slab 2 multiplier"
              />
            </Box>

            {/* Submit */}
            <Box mt="1rem">
              <Button
                type="submit"
                width="100%"
                height="3rem"
                rightIcon={<FaArrowRight />}
                isLoading={isSubmitting}
                loadingText="Updating..."
                style={{
                  background: theme.colors.primary.main,
                  color: theme.colors.bg.main,
                }}
              >
                Update Ride Fare
              </Button>
            </Box>

            {/* Status */}
            <Box minHeight="3.25rem">
              {createStatus === "created" && (
                <Alert status="success">
                  <AlertIcon />
                  Ride fare updated successfully.
                </Alert>
              )}

              {createStatus === "error" && (
                <Alert status="error" borderRadius="4px">
                  <AlertIcon />
                  Oops! Something went wrong.
                </Alert>
              )}
            </Box>
          </Flex>
        </form>
      </Box>
    </Box>
  );
};

export default ModifyRideFare;
